import * as Sentry from "@sentry/angular-ivy";
import { HttpErrorResponse } from '@angular/common/http';

import { environment } from '@utility/app.util';

interface SentryUser {
  email?: string;
  id?: string | number;
  username?: string;
}

/**
 * Interface for error context with dynamic properties
 */
interface ErrorContext {
  message?: string;
  url?: string;
  method?: string;
  status?: number;
  statusText?: string;
  type?: string;
  title?: string;
  details?: any;
  payload?: any;
  [key: string]: any; // Allow dynamic properties
}

function isProductionEnv(): boolean {
  return environment.production === true
    || environment.production === 'true'
    || environment.env === 'production';
}

function resolveSentrySampleRates(): { tracesSampleRate: number; replaysSessionSampleRate: number } {
  if (isProductionEnv()) {
    return {
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.01,
    };
  }

  return {
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
  };
}

/**
 * Convert any value (including nested JSON objects) to an Error object
 * @param error - The error value to convert (can be Error, string, object, etc.)
 * @returns Error object with stringified content
 */
function convertToError(error: unknown): Error {
  // If it's already an Error, return it
  if (error instanceof Error) {
    return error;
  }

  // If it's a string, create Error from string
  if (typeof error === 'string') {
    return new Error(error);
  }

  // If it's an object (including nested JSON), stringify it
  if (typeof error === 'object' && error !== null) {
    try {
      const errorString = JSON.stringify(error, null, 2);
      return new Error(`Error Object: ${errorString}`);
    } catch (stringifyError) {
      // If JSON.stringify fails, use toString()
      return new Error(`Error Object: ${String(error)}`);
    }
  }

  // For any other type (number, boolean, etc.), convert to string
  return new Error(String(error));
}

/**
 * Extract standard properties from error context
 * @param context - Error context with dynamic properties
 * @returns Object with standard properties and remaining dynamic properties
 */
function extractErrorProperties(context: ErrorContext): {
  standardProps: { title?: string; message?: string };
  dynamicProps: Record<string, any>;
} {
  const { title, message, ...dynamicProps } = context;
  return {
    standardProps: { title, message },
    dynamicProps
  };
}

/**
 * Build Sentry context with dynamic properties
 * @param context - Error context with dynamic properties
 * @returns Sentry context object with fingerprint and tags
 */
function buildSentryContext(context: ErrorContext): {
  fingerprint?: string[];
  tags?: Record<string, string>;
  contexts?: Record<string, any>;
} {
  const { standardProps, dynamicProps } = extractErrorProperties(context);
  const sentryContext: any = {};

  // Set fingerprint based on title for error grouping
  if (standardProps.title) {
    sentryContext.fingerprint = [standardProps.title];
    sentryContext.tags = {
      errorTitle: standardProps.title
    };
  }

  // Add dynamic properties as context
  if (Object.keys(dynamicProps).length > 0) {
    sentryContext.contexts = {
      errorContext: dynamicProps
    };
  }

  return sentryContext;
}

/**
 * Capture exception with dynamic properties and automatic grouping
 * @param error - Error object or context with dynamic properties
 */
function captureExceptionFunc(error: unknown): void {
  try {
    // Convert the error to an Error object (handles nested JSON, strings, objects, etc.)
    const errorObject = convertToError(error);

    // Build Sentry context with dynamic properties
    const sentryContext = buildSentryContext(error as ErrorContext);

    // Only capture exceptions in production or staging environments
    if (environment.env !== 'local') {
      console.log('Capturing exception in Sentry:', errorObject);
      console.log('Sentry context:', sentryContext);

      // Capture with context if available
      if (Object.keys(sentryContext).length > 0) {
        Sentry.captureException(errorObject, sentryContext);
      } else {
        Sentry.captureException(errorObject);
      }
    } else {
      console.log('Capturing exception in Sentry (local env):', errorObject);
      console.log('Sentry context (local env):', sentryContext);
      console.warn('Sentry error capture (local env):', errorObject);
    }
  } catch (sentryError) {
    console.error('Failed to capture exception in Sentry:', sentryError);
  }
}

function sentryInitFunc(): void {
  const isLocalEnv = environment.env === 'local';
  const sentryDsn = environment.sentryDSN || '';

  if (isLocalEnv || !sentryDsn) {
    return;
  }

  const sampleRates = resolveSentrySampleRates();
  const isDebugEnabled = !isProductionEnv();

  Sentry.init({
    dsn: sentryDsn,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: ["*"],
      }),
      Sentry.replayIntegration({
        // Disable masking of text content (shows actual text instead of *)
        maskAllText: false,
        // Disable blocking of media (shows images/videos)
        blockAllMedia: false,
        // Mask sensitive input fields for security
        mask: [
          'input[type="password"]',
          '.credit-card-input',
          '[data-sentry-mask]',
          // Social integration API keys
          'input[formcontrolname="apiKey"]',
          'input[formcontrolname="api_key"]',
          // Generic API key/token/secret inputs
          'input[placeholder*="API"]',
          'input[placeholder*="api"]',
          'input[placeholder*="key" i]',
          'input[placeholder*="token" i]',
          'input[placeholder*="secret" i]',
          // 2FA/OTP authentication codes
          'input[formcontrolname="code"]',
          // Email addresses
          'input[formcontrolname="email"]',
          'input[type="email"]',
          // Phone numbers
          'input[formcontrolname="phone"]',
          'ngx-intl-tel-input input',
        ],
      }),
    ],
    ignoreErrors: [],
    tracesSampleRate: sampleRates.tracesSampleRate,
    debug: isDebugEnabled,
    attachStacktrace: true,
    environment: environment.env,
    replaysSessionSampleRate: sampleRates.replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,
    // Normalize depth for context data (default is 3, increase to capture deeper objects)
    normalizeDepth: 10,
    // Maximum length for values (default is 250, increase to capture full error messages)
    maxValueLength: 5000,
    // Serialize console breadcrumb arguments to show full objects
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'console' && hint?.input) {
        try {
          // Serialize all console arguments to show full objects
          const serializedArgs = hint.input.map((arg: any) => {
            if (typeof arg === 'object' && arg !== null) {
              return JSON.stringify(arg, null, 2);
            }
            return arg;
          });
          breadcrumb.message = serializedArgs.join(' ');
        } catch (e) {
          // If serialization fails, keep original breadcrumb
        }
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      try {
        const originalException = hint?.originalException as any;
        const contextStatus = Number(event?.contexts?.errorContext?.status);
        const statusFromContext = Number.isNaN(contextStatus) ? undefined : contextStatus;
        const statusFromException = typeof originalException?.status === 'number'
          ? originalException.status
          : undefined;
        const status = statusFromContext ?? statusFromException;

        if (originalException instanceof HttpErrorResponse) {
          if (status !== 0 && status < 500) return null;
        }

        if (typeof status === 'number') {
          if (status !== 0 && status < 500) return null;
        }
      } catch {
      }

      return event;
    },
  });
}

function setSentryUserFunc(user: SentryUser): void {
  Sentry.setUser(user);
}

function removeSentryUserFunc(): void {
  Sentry.setUser(null);
}

export const setSentryUser = addTryCatch(setSentryUserFunc);
export const removeSentryUser = addTryCatch(removeSentryUserFunc);
export const sentryInit = addTryCatch(sentryInitFunc);
export const captureException = addTryCatch(captureExceptionFunc);
export { convertToError, buildSentryContext, extractErrorProperties };
export type { ErrorContext };

function addTryCatch<T extends (...args: any[]) => any>(fn: T): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return function (...args: Parameters<T>) {
    try {
      return fn(...args);
    } catch (error) {
      console.error("Error occurred in sentry:", error);
      return undefined;
    }
  };
}
